import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { loadTurnstile, turnstileSiteKey } from "@/lib/turnstile";

export type TurnstileWidgetHandle = {
  /** Discards the current token and re-runs the challenge. Tokens are
   * single-use, so callers must reset after a rejected submit. */
  reset: () => void;
};

type TurnstileWidgetProps = {
  /** Labels the challenge in Turnstile analytics. */
  action?: string;
  className?: string;
  /** Fired when the widget errors or its token expires — clear the token. */
  onUnavailable?: () => void;
  onVerify: (token: string) => void;
};

/**
 * Cloudflare Turnstile challenge, rendered explicitly.
 *
 * Renders nothing when no site key is configured, mirroring the passthrough
 * shape of `components/analytics/posthog-provider.tsx`. The widget is mounted
 * from an effect so it never runs during prerender — `/contact` is a
 * prerendered route.
 */
export const TurnstileWidget = forwardRef<
  TurnstileWidgetHandle,
  TurnstileWidgetProps
>(function TurnstileWidget(
  { action, className, onUnavailable, onVerify },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  // Held in a ref so re-created callbacks never re-run the mount effect (which
  // would tear down and re-render the challenge on every keystroke).
  const handlers = useRef({ onUnavailable, onVerify });
  handlers.current = { onUnavailable, onVerify };

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current) {
          window.turnstile?.reset(widgetIdRef.current);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    // Copied to a local so the non-null narrowing survives into the async
    // callback below — TypeScript will not carry it across a closure for an
    // imported binding.
    const sitekey = turnstileSiteKey;

    if (!sitekey) {
      return;
    }

    let cancelled = false;

    loadTurnstile()
      .then((api) => {
        if (cancelled || !containerRef.current || widgetIdRef.current) {
          return;
        }

        widgetIdRef.current =
          api.render(containerRef.current, {
            action,
            callback: (token) => handlers.current.onVerify(token),
            "error-callback": () => {
              setUnavailable(true);
              handlers.current.onUnavailable?.();
            },
            "expired-callback": () => handlers.current.onUnavailable?.(),
            sitekey,
            size: "flexible",
            theme: "light",
          }) ?? null;
      })
      .catch(() => {
        if (!cancelled) {
          setUnavailable(true);
          handlers.current.onUnavailable?.();
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current) {
        window.turnstile?.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [action]);

  if (!turnstileSiteKey) {
    return null;
  }

  return (
    <div className={className}>
      <div ref={containerRef} />
      {unavailable ? (
        <p className="text-[13px] text-destructive">
          We couldn't load the verification challenge, so the form can't be
          submitted. Please email{" "}
          <a className="underline" href="mailto:support@postdaily.app">
            support@postdaily.app
          </a>{" "}
          instead.
        </p>
      ) : null}
    </div>
  );
});
